import type React from "react";

import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";

interface SelectFieldWrapperProps {
  id: string;
  label: string;
  error: string;
  helpText: React.ReactNode;
  errorId: string;
  helpTextId: string;
  isDisabled: boolean;
  required: boolean;
  testIdPrefix?: string;
  labelProps?: Omit<
    React.ComponentProps<typeof FieldLabel>,
    "htmlFor" | "children"
  >;
  className?: string;
  children: React.ReactNode;
}

export function SelectFieldWrapper({
  id,
  label,
  error,
  helpText,
  errorId,
  helpTextId,
  isDisabled,
  required,
  testIdPrefix,
  labelProps,
  className,
  children,
}: SelectFieldWrapperProps) {
  const hasField = !!(label || error || helpText);

  if (!hasField) {
    return (
      <div
        className={className}
        {...(testIdPrefix
          ? { "data-testid": `${testIdPrefix}-select-container-wrapper` }
          : {})}
      >
        {children}
      </div>
    );
  }

  return (
    <Field
      data-disabled={isDisabled || undefined}
      data-invalid={!!error || undefined}
      className={className}
      {...(testIdPrefix
        ? { "data-testid": `${testIdPrefix}-select-container-wrapper` }
        : {})}
    >
      {label && (
        <FieldLabel
          htmlFor={id}
          {...labelProps}
          {...(testIdPrefix
            ? { "data-testid": `${testIdPrefix}-input-label` }
            : {})}
        >
          {label}
          {required && (
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          )}
        </FieldLabel>
      )}
      <FieldContent>
        {children}
        {!!error && (
          <FieldError
            id={errorId}
            {...(testIdPrefix
              ? { "data-testid": `${testIdPrefix}-select-error` }
              : {})}
          >
            {error}
          </FieldError>
        )}
        {helpText && (
          <FieldDescription
            id={helpTextId}
            {...(testIdPrefix
              ? { "data-testid": `${testIdPrefix}-select-help-text` }
              : {})}
          >
            {helpText}
          </FieldDescription>
        )}
      </FieldContent>
    </Field>
  );
}
